// 파일럿(시 단위) 권역의 행정경계 데이터 — vuski/admdongkor GeoJSON 기반.
//
// GeoJSON은 [lng,lat] 순, Naver Maps SDK 및 기존 코드 컨벤션은 [lat,lng] 순.
// 로드 시점에 변환해서 캐시. 좌표 소수점 5자리(~1m)로 이미 라운드됨.
//
//   R018 김해시   — 3개 하위구역(북부/중부/남부),  data/gimhae.json
//   R019 남양주시 — 8개 하위구역(진접·오남·화도·와부·별내·퇴계원·호평·다산),
//                   data/namyangju.json (N-CARE 생활권역 편제와 1:1)
//
// 새 파일럿을 붙일 때는 ① 같은 스키마의 GeoJSON을 data/ 에 넣고 ② 아래 SOURCES 에
// 한 줄, ③ SUB_AREA_COLORS 에 구역 색을 추가하면 된다 — 렌더러(useNaverOverlays)는
// 권역 코드로만 조회하므로 코드 수정이 필요 없다. 데이터가 없는 권역은 기존처럼
// bbox 사각형 경계로 폴백한다.
//
// 폴리곤 형상: 각 구역이 Polygon 또는 MultiPolygon(분리된 조각)일 수 있으므로,
// 렌더러가 항상 MultiPolygonPath = LatLng[][][] 형태로 다룬다.

import gimhaeRaw from "../../data/gimhae.json";
import namyangjuRaw from "../../data/namyangju.json";

export type LatLng = [number, number];        // [lat, lng]
export type Ring = LatLng[];                  // 폴리곤 링(외곽 or 홀)
export type PolygonPath = Ring[];             // 한 폴리곤 = 외곽 + 홀들
export type MultiPolygonPath = PolygonPath[]; // 다중 폴리곤

export interface AnchorSpec {
  name: string;
  point: LatLng;
  coverage_m: number;
}

// 장기요양 거점 — 사회복지법인 시설 중 규모·평가 상위. 구역별 1개.
// 비장기요양(복지관) 거점과는 시설 성격이 달라 두 세트를 함께 렌더한다.
export interface LongtermAnchorSpec extends AnchorSpec {
  corporation?: string;      // 운영 사회복지법인명
  grade_2025?: string;       // 2025 장기요양기관 평가 등급/점수 (예: "A", "94.2")
  capacity?: number;         // 정원 (규모 지표)
}

export interface SubAreaSpec {
  key: string;               // "북부" | "진접" …  (Region#sub_areas_list 와 같은 어휘)
  name: string;              // 표시용 풀 네임 ("북부 (진영·한림·생림)")
  paths: MultiPolygonPath;
  color: string;             // 하이라이트/필 색
  anchor?: AnchorSpec;                   // 비장기요양(복지관)
  longterm_anchor?: LongtermAnchorSpec;  // 장기요양(사회복지법인 시설)
  dongs: string[];           // 소속 읍면동 목록 (툴팁·디버깅용)
}

export interface PilotBoundary {
  regionCode: string;
  outer: MultiPolygonPath;
  subAreas: SubAreaSpec[];
}

// ── 구역 색: 무채도 낮은 톤. 진한 지도 위에서도 조용히 뒤로 물러남. ────
// 지도 폴리곤·커버리지·앵커와 어르신 마커의 구역 배지, 범례 pill이 모두 이 한 곳을
// 읽는다 — 색이 어긋나면 "이 어르신은 저 구역"을 눈으로 잇지 못하기 때문.
// 구역 이름은 파일럿 간 겹치지 않아 권역 코드 없이 이름만으로 조회한다.
export const SUB_AREA_COLORS: Record<string, string> = {
  // 김해 (R018)
  "북부": "#5B7FE0",  // muted indigo — 진영·한림·생림 (농촌 혼재)
  "중부": "#E27A55",  // muted terra  — 본시가지·대동·상동·진례 (도심)
  "남부": "#4FA379",  // muted moss   — 장유·주촌 (신도시)
  // 남양주 (R019)
  "진접":   "#5B7FE0",  // indigo
  "오남":   "#3FA0B8",  // teal
  "화도":   "#E27A55",  // terra
  "와부":   "#4FA379",  // moss
  "별내":   "#B072D0",  // orchid
  "퇴계원": "#D9A227",  // amber
  "호평":   "#E0616B",  // rose
  "다산":   "#7B9B3F",  // olive
};

const SOURCES: Record<string, any> = {
  R018: gimhaeRaw,
  R019: namyangjuRaw,
};

// ── GeoJSON → LatLng 변환 헬퍼 ────────────────────────────────
function ringFrom(coords: number[][]): Ring {
  return coords.map(([lng, lat]) => [lat, lng] as LatLng);
}
function polyFrom(rings: number[][][]): PolygonPath {
  return rings.map(ringFrom);
}
function multiFrom(geom: any): MultiPolygonPath {
  if (!geom) return [];
  if (geom.type === "Polygon")      return [polyFrom(geom.coordinates)];
  if (geom.type === "MultiPolygon") return (geom.coordinates as number[][][][]).map(polyFrom);
  return [];
}

function anchorFrom(a: any, fallbackCoverage = 6000): AnchorSpec | undefined {
  if (!a) return undefined;
  return { name: a.name, point: [a.lat, a.lng] as LatLng, coverage_m: a.coverage_m ?? fallbackCoverage };
}

function parse(regionCode: string, raw: any): PilotBoundary {
  const features = (raw?.features ?? []) as any[];
  const outerF = features.find(f => f?.properties?.kind === "outer");
  const subAreas = features
    .filter(f => f?.properties?.kind === "sub_area")
    .map(f => {
      const p = f.properties;
      const la = p.longterm_anchor;
      return {
        key:   p.key,
        name:  p.name,
        paths: multiFrom(f.geometry),
        color: SUB_AREA_COLORS[p.key] ?? "#8890a5",
        anchor: anchorFrom(p.anchor),
        longterm_anchor: la ? {
          ...anchorFrom(la)!,
          corporation: la.corporation,
          grade_2025:  la.grade_2025,
          capacity:    la.capacity,
        } : undefined,
        dongs: p.dongs ?? [],
      } as SubAreaSpec;
    });
  return { regionCode, outer: outerF ? multiFrom(outerF.geometry) : [], subAreas };
}

// 파싱은 비싸므로(수만 좌표) 권역별 1회만 하고 캐시한다.
const cache = new Map<string, PilotBoundary | null>();

/** 권역 코드로 파일럿 경계 데이터 조회. 데이터가 없는 권역은 null(→ bbox 폴백). */
export function boundaryFor(regionCode: string | null | undefined): PilotBoundary | null {
  const code = regionCode ?? "";
  if (cache.has(code)) return cache.get(code)!;
  const raw = SOURCES[code];
  const parsed = raw ? parse(code, raw) : null;
  cache.set(code, parsed);
  return parsed;
}

/** 이 권역이 폴리곤 경계를 가진 파일럿인지. */
export function hasPilotBoundary(regionCode: string | null | undefined): boolean {
  return boundaryFor(regionCode) != null;
}

// ── 헬퍼: MultiPolygon의 bounding box (fitBounds 용) ─────────
export function boundsOfMulti(mp: MultiPolygonPath): { south: number; north: number; west: number; east: number } {
  let south =  Infinity, north = -Infinity, west =  Infinity, east = -Infinity;
  for (const poly of mp) for (const ring of poly) for (const [lat, lng] of ring) {
    if (lat < south) south = lat;
    if (lat > north) north = lat;
    if (lng < west)  west  = lng;
    if (lng > east)  east  = lng;
  }
  return { south, north, west, east };
}

// 하위 호환 alias — 기존 boundsOf(path) 호출부가 있을 수 있음.
export const boundsOf = boundsOfMulti;

// ── 점-폴리곤 포함 판정 (ray casting) ──────────────────────
// 특정 어르신 좌표가 어느 구역에 속하는지 판별.
// 홀(hole)은 배제, MultiPolygon은 어느 하나라도 포함하면 true.
function pointInRing(lat: number, lng: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i];
    const [yj, xj] = ring[j];
    const intersect =
      (yi > lat) !== (yj > lat) &&
      lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInPolygon(lat: number, lng: number, poly: PolygonPath): boolean {
  if (poly.length === 0) return false;
  if (!pointInRing(lat, lng, poly[0])) return false;
  for (let i = 1; i < poly.length; i++) {
    if (pointInRing(lat, lng, poly[i])) return false; // hole
  }
  return true;
}

export function pointInMultiPolygon(lat: number, lng: number, mp: MultiPolygonPath): boolean {
  for (const poly of mp) if (pointInPolygon(lat, lng, poly)) return true;
  return false;
}

/** 좌표가 속한 하위 구역 key 반환 (권역에 경계 데이터가 없거나 밖이면 null). */
export function subAreaForCoord(
  regionCode: string | null | undefined,
  lat: number | null | undefined,
  lng: number | null | undefined,
): string | null {
  if (lat == null || lng == null) return null;
  const b = boundaryFor(regionCode);
  if (!b) return null;
  for (const s of b.subAreas) {
    if (pointInMultiPolygon(lat, lng, s.paths)) return s.key;
  }
  return null;
}
